import { Button, TableItem } from 'components/atoms';
import LabelReportButtonDownload from 'components/molecules/buttons/reportDownload/LabelReportButtonDownload';
import SendButtonEmail from 'components/molecules/buttons/sendEmail/SendButtonEmail';
import LabeledEmailSend from 'components/molecules/labeledStatus/LabeledEmailSend';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { useCustomParamId } from 'hooks/useParamId';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { t } from 'i18next';
import { PiCaretLeftBold, PiClock } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { DOCUMENT_TYPE } from 'services/email/types';
import {
  useGetHistoricalReportsByLabelInfinite,
} from 'services/label-report';
import { EMAIL_SEND } from 'services/types';
import { formatCurrency } from 'utils/global/currency.utils';
import { getMaxFormattedLength } from 'utils/global/getMaxFormattedLength.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';

const LabelReportsHistory = () => {
  const labelId = useCustomParamId('id', '/label');

  usePermissionGuard('LabelReportOrderPermissionsLIST**');

  const navigate = useNavigate();

  const labelReports = useGetHistoricalReportsByLabelInfinite(labelId);

  const tableParams = useInfiniteTable(labelReports, { selectable: false });

  const allAmountLength = getMaxFormattedLength(
    tableParams.data?.nodes ?? [],
    e => e?.amountEarned
  );

  const allAmountLabelLength = getMaxFormattedLength(
    tableParams.data?.nodes ?? [],
    e => e?.amountForLabel
  );

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t('history.label-raports')}
          size='compact'
          customBackItem={
            <Button
              isClear
              onlyIcon
              onClick={() => navigate(`/label/${labelId}/reports`)}>
                            <Button.Description description={t('button.back')} />

                            <Button.Icon icon={<PiCaretLeftBold />} />
            </Button>
          }
        />
      }>
      <Table
        id={'LabelReports-history-file3'}
        {...tableParams}
        size='compact'
        columns={[
          {
            label: t('input.label.startSettlementPeriod'),
            renderCell: ({ startSettlementPeriodName }) => (
              <TableItem>
                <TableItem.Text
                  text={getSettlementPeriodName(startSettlementPeriodName)}
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.endSettlementPeriod'),
            renderCell: ({ endSettlementPeriodName }) => (
              <TableItem>
                <TableItem.Text
                  text={getSettlementPeriodName(endSettlementPeriodName)}
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.number'),
            renderCell: ({ labelReportNumber }) => (
              <TableItem>
                <TableItem.Text text={labelReportNumber ?? undefined} />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.amountEarned'),
            renderCell: ({ amountEarned }) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(amountEarned, {
                    length: allAmountLength,
                  })}
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.amountForLabel'),
            renderCell: ({ amountForLabel }) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(amountForLabel, {
                    length: allAmountLabelLength,
                  })}
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('label.table.compliance'),
            renderCell: ({ isOutdated }) => (
              <TableItem>
                {isOutdated && (
                  <TableItem.Label color='orange' size='compact'>
                    <PiClock />
                    {t('label.compliance.outdated')}
                  </TableItem.Label>
                )}
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.email-sender'),
            renderCell: ({ id, sendStatus }) => (
              <TableItem>
                <div className='flex items-center gap-2'>
                  {sendStatus !== EMAIL_SEND.notSend && (
                    <LabeledEmailSend status={sendStatus} />
                  )}
                  {sendStatus !== EMAIL_SEND.planned && (
                    <SendButtonEmail
                      documentId={id}
                      documentType={DOCUMENT_TYPE.labelReport}
                      refetch={labelReports.refetch}
                      status={sendStatus}
                    />
                  )}
                </div>
              </TableItem>
            ),
            resize: true,
          },
          {
            label: '',
            renderCell: ({
              id,
              filePath,
              labelReportNumber,
              detailedFilePath,
            }) => (
              <TableItem justifyPosition='end'>
                <div className='flex gap-1'>
                  <LabelReportButtonDownload
                    id={id}
                    name={labelReportNumber ?? filePath ?? ''}
                    canGenerateDetailedReport={!!detailedFilePath}
                  />
                </div>
              </TableItem>
            ),
          },
        ]}
      />
    </TopbarPage>
  );
};

export default LabelReportsHistory;
